import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pager, TypeBadge, Changes, TH } from "@/components/common";
import { api, fmtDate, fmtQty, MOVE_TYPES } from "@/lib/api";

const ALL = "__all__";
const EMPTY = { code: "", type: "", user_id: "", date_from: "", date_to: "" };

export function MovementHistory({ refresh }) {
  const [f, setF] = useState(EMPTY);
  const [code, setCode] = useState("");
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, page_size: 25 });

  useEffect(() => { api.get("/movements/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => { const t = setTimeout(() => { setF((p) => ({ ...p, code })); setPage(1); }, 300); return () => clearTimeout(t); }, [code]);
  const load = useCallback(() => {
    api.get("/movements", { params: { ...f, page, page_size: 25 } }).then((r) => setData(r.data));
  }, [f, page]);
  useEffect(() => { load(); }, [load, refresh]);

  const set = (k, v) => { setF({ ...f, [k]: v }); setPage(1); };
  const reset = () => { setF(EMPTY); setCode(""); setPage(1); };

  return (
    <div className="rounded-md border bg-white shadow-sm" data-testid="movement-history">
      <div className="grid grid-cols-1 gap-3 border-b p-4 sm:grid-cols-2 xl:grid-cols-6">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code article ou kit" className="h-12 font-mono" data-testid="history-filter-code" />
        <Select value={f.type || ALL} onValueChange={(v) => set("type", v === ALL ? "" : v)}>
          <SelectTrigger className="h-12" data-testid="history-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tous types</SelectItem>{Object.entries(MOVE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.user_id || ALL} onValueChange={(v) => set("user_id", v === ALL ? "" : v)}>
          <SelectTrigger className="h-12" data-testid="history-filter-user"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tous utilisateurs</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} className="h-12" aria-label="Du" data-testid="history-filter-date-from" />
        <Input type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} className="h-12" aria-label="Au" data-testid="history-filter-date-to" />
        <Button variant="outline" className="h-12 gap-2" onClick={reset} data-testid="history-filter-reset"><X className="h-4 w-4" /> Réinitialiser</Button>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid="history-table">
          <TableHeader><TableRow>
            <TableHead className={TH}>Date</TableHead><TableHead className={TH}>Type</TableHead><TableHead className={TH}>Code</TableHead><TableHead className={TH}>Désignation</TableHead>
            <TableHead className={`${TH} text-right`}>Quantité</TableHead><TableHead className={TH}>Stock (avant → après)</TableHead><TableHead className={TH}>Utilisateur</TableHead><TableHead className={TH}>Note</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.items.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-slate-500" data-testid="history-empty">Aucun mouvement.</TableCell></TableRow>}
            {data.items.map((m) => (
              <TableRow key={m.id} data-testid={`history-row-${m.id}`}>
                <TableCell className="whitespace-nowrap font-mono text-xs">{fmtDate(m.date)}</TableCell>
                <TableCell><TypeBadge type={m.type} /></TableCell>
                <TableCell className="font-mono font-semibold">{m.code}</TableCell>
                <TableCell className="min-w-[180px]">{m.designation}</TableCell>
                <TableCell className="text-right font-mono">{m.type === "ajustement" ? "= " : ""}{fmtQty(m.quantity)}</TableCell>
                <TableCell><Changes changes={m.changes} /></TableCell>
                <TableCell className="whitespace-nowrap text-sm">{m.user_name}</TableCell>
                <TableCell className="max-w-[220px] text-sm text-slate-500">{m.note || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Pager page={page} pageSize={data.page_size} total={data.total} onPage={setPage} testid="history-pager" />
    </div>
  );
}
